/**
 * Resolve Shipping Failure Dialog
 *
 * Allows coordinators to resolve shipping failures with a resolution type.
 * FOLLOWS: ResolveDialog.tsx patterns
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Truck, RefreshCw, XCircle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useResolveShippingFailure } from '@/features/packing-station/hooks/useShipping';
import { toast } from 'sonner';

type Resolution = 'MANUAL_SHIP' | 'RETRY_SUCCESS' | 'CANCELLED';

interface ResolveShippingFailureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  failureId: string;
  locationId: string;
  orderName?: string;
}

export function ResolveShippingFailureDialog({
  open,
  onOpenChange,
  failureId,
  locationId,
  orderName,
}: ResolveShippingFailureDialogProps) {
  const { t } = useTranslation('production');
  const [resolution, setResolution] = useState<Resolution>('MANUAL_SHIP');
  const [notes, setNotes] = useState('');

  const resolveFailure = useResolveShippingFailure();

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setResolution('MANUAL_SHIP');
      setNotes('');
    }
    onOpenChange(newOpen);
  };

  const handleResolve = async () => {
    try {
      await resolveFailure.mutateAsync({
        failureId,
        locationId,
        resolution,
        notes: notes || undefined,
      });

      toast.success(
        resolution === 'CANCELLED' ? t('issues.shipping.resolve.orderCancelled') : t('issues.shipping.resolve.resolved')
      );
      handleOpenChange(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('issues.shipping.resolve.failed');
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('issues.shipping.resolve.title')}</DialogTitle>
          <DialogDescription>
            {orderName
              ? t('issues.shipping.resolve.descriptionWithOrder', { orderName })
              : t('issues.shipping.resolve.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <RadioGroup
            value={resolution}
            onValueChange={(v) => setResolution(v as Resolution)}
            className="space-y-3"
          >
            <div className="flex items-center space-x-3 rounded-lg border p-4 min-h-16 hover:bg-muted/50">
              <RadioGroupItem value="MANUAL_SHIP" id="manual-ship" />
              <Label htmlFor="manual-ship" className="flex items-center gap-2 cursor-pointer">
                <Truck className="h-5 w-5 text-green-600" aria-hidden="true" />
                <div>
                  <p className="font-medium">{t('issues.shipping.resolve.manualShip')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('issues.shipping.resolve.manualShipDesc')}
                  </p>
                </div>
              </Label>
            </div>

            <div className="flex items-center space-x-3 rounded-lg border p-4 min-h-16 hover:bg-muted/50">
              <RadioGroupItem value="RETRY_SUCCESS" id="retry-success" />
              <Label htmlFor="retry-success" className="flex items-center gap-2 cursor-pointer">
                <RefreshCw className="h-5 w-5 text-blue-600" aria-hidden="true" />
                <div>
                  <p className="font-medium">{t('issues.shipping.resolve.retrySuccess')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('issues.shipping.resolve.retrySuccessDesc')}
                  </p>
                </div>
              </Label>
            </div>

            <div className="flex items-center space-x-3 rounded-lg border p-4 min-h-16 hover:bg-muted/50">
              <RadioGroupItem value="CANCELLED" id="cancelled" />
              <Label htmlFor="cancelled" className="flex items-center gap-2 cursor-pointer">
                <XCircle className="h-5 w-5 text-red-600" aria-hidden="true" />
                <div>
                  <p className="font-medium">{t('issues.shipping.resolve.cancelOrder')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('issues.shipping.resolve.cancelOrderDesc')}
                  </p>
                </div>
              </Label>
            </div>
          </RadioGroup>

          <div className="space-y-2">
            <Label htmlFor="notes">{t('issues.shipping.resolve.notesLabel')}</Label>
            <Textarea
              id="notes"
              placeholder={t('issues.shipping.resolve.notesPlaceholder')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t('issues.shipping.resolve.cancelBtn')}
          </Button>
          <Button
            onClick={handleResolve}
            disabled={resolveFailure.isPending}
            variant={resolution === 'CANCELLED' ? 'destructive' : 'default'}
            className="h-12 min-w-[100px]"
          >
            {resolveFailure.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : resolution === 'CANCELLED' ? (
              t('issues.shipping.resolve.cancelOrder')
            ) : (
              t('issues.resolve')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
